


import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useFillBottom, useReferrerState } from 'hooks';
import { useTagProducts } from 'hooks/api/useTagProducts';
import { useProducts } from 'hooks/api/useProducts';
import { ROUTES } from 'routes';
import { SHOP_PRODUCT_TYPE, SHOP_TYPE_PARAM, isShopProductType, shopTypeLabel, type ShopProductType } from 'constants/tagShopProductTypes';

import { useTheme, Grid, Fade, Stack, Typography, IconButton } from '@mui/material';
import { ShieldCheckIcon, ShieldAlertIcon, RadarIcon, KeyRoundIcon, ThermometerIcon, DropletsIcon, GaugeIcon, XIcon, PackageSearchIcon } from 'lucide-react';

import { Button, useScrollFade } from '@repo/ui';
import { ActionLine, ProductCell, TagsShopProductPreview, CategoryCell, EmptyState } from 'components';
import { CustomTagModal } from './CustomTagModal';
import { ShopSkeleton } from './components/ShopSkeleton';

import { multiSort, toDollar } from 'utils';

import { readRange, LEAF_BLURB, isFormHidden, isTagFormFactor, type TagCapability } from 'constants/tagCatalog';

// VRG-152 — Capability filter as StatusChip-style toggles (icon + family colour), one
// chip per ATOMIC capability leaf (so "Tamper" matches ANY product with tamper, not just
// the pre-combined "Verify + Tamper" label). Connect is excluded — it's the baseline
// every tag has, so it would match everything. Family colours: auth (Verify/Vault) =
// purple, Trace = blue, Tamper = red, Sense (Temperature/Moisture/Pressure) = orange.
const CAP_ICON = { size: 14, strokeWidth: 1.5 };

// VRG-156 — Scan-range buckets = NFC PHONE-TAP range (near-field, size-driven). RFID
// far-field (~10 m) is a separate modality, not a bucket here (that's the Trace capability).
const SCAN_RANGE_BUCKETS = ['Touch (~1 cm)', 'Tap (~2–4 cm)'];
// Which scan-range buckets a product falls in, from its sizes' base (Verify) read range.
const scanBucketsForSizes = (sizes: string[]): Set<string> => {
  const set = new Set<string>();
  for (const s of (sizes ?? [])) {
    if (!s) continue;
    const r = readRange('Verify' as TagCapability, s);
    if (/touch/i.test(r)) set.add('Touch (~1 cm)');
    else if (/tap/i.test(r)) set.add('Tap (~2–4 cm)');
  }
  return set;
};

// Type definitions
interface SortItem {
  field: string;
  sort: 'asc' | 'desc';
}

interface FilterItem {
  field: string;
  operator: 'isAnyOf' | '>=' | '<=';
  value: string[] | number;
}

interface Column {
  field: string;
  headerName: string;
  type: string;
  width?: number;
  pinned?: string;
  searchParams?: any;
  renderCell?: (params: any) => React.ReactNode;
}

const PAGE = 12;

export const Shop: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  // Derived from the Tags page URL this tab renders under — "← Back to Tags".
  const referrer = useReferrerState();

  /**
   * Type filter, from the URL (`/tags/shop?type=Hardware`) — Shopify's per-product `Type`
   * field, surfaced by the API as `category_type`. This is what "Shop readers" in
   * `ReaderSetupPanel` navigates to.
   *
   * It lives in the URL rather than in `filters` below because it arrives from OUTSIDE this
   * component — a deep link — and the filters drawer stages its own copy of `filters` at
   * mount, so a filter pushed in later would apply to the grid while the drawer's count
   * badge kept saying zero. A URL param the shop reads directly is honest in both cases: it
   * survives a reload, it can be linked to, and it is shown as its own removable chip below.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = searchParams.get(SHOP_TYPE_PARAM);
  // VRG-266 -- "we can make that" intake. Opened from the end-of-grid card AND from the
  // empty state, which is the same offer at the two moments the catalog has failed you.
  const [customTagOpen, setCustomTagOpen] = useState(false);
  const clearTypeFilter = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(SHOP_TYPE_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const [search, setSearch] = useState<string>('');
  const [filters, setFilters] = useState<FilterItem[]>([]);
  const [sort, setSort] = useState<SortItem[]>([]);

  // Progressive render state
  const [visibleCount, setVisibleCount] = useState<number>(PAGE);

  // Infinite-scroll sentinel
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Memoize params to prevent unnecessary re-renders
  const tagProductsParams = useMemo(() => ({ limit: 250 }), []);
  const { data, isLoading } = useTagProducts(tagProductsParams);
  const allProducts = data?.products || [];

  // Everything downstream — the grid, the search, the facet options — works off the
  // type-scoped set, so with `?type=Hardware` the Size/Capability facets offer what the
  // readers actually have rather than the whole tag catalog's values matching nothing.
  const products = useMemo(
    () => {
      /*
       * Hidden forms come out FIRST, before the type scope and before the facets
       * derive anything from the set. A form filtered later would still have voted
       * on the Size and Capability options, so the drawer would offer a facet value
       * that matches nothing visible — the same silent-empty-facet failure the
       * comment below records, arrived at from the other direction.
       */
      const listed = allProducts.filter(
        p => !(isTagFormFactor((p as any).form_factor) && isFormHidden((p as any).form_factor)),
      );
      const scoped = typeFilter
        ? listed.filter(p => isShopProductType(p.category_type, typeFilter as ShopProductType))
        : listed;

      /*
       * Derived facet fields. The API returns sizes per VARIANT only — there is no
       * product-level `sizes` column — but two facets were written against `product.sizes`
       * and so were silently dead: `sizeOptions` built its option list from an always-empty
       * set (the Size facet rendered with nothing to pick), and the `scanRange` matcher
       * derived its buckets from the same empty array, so Scan range matched nothing. Both
       * failed SILENTLY, because an empty facet looks like "no options for this catalog"
       * rather than like a bug.
       *
       * Derived here, once, rather than in each consumer: the option list and the matcher
       * have to agree about what a product's sizes ARE, and the way they drift is exactly
       * this — one reads a field the other never populates.
       *
       * `on_metal` is a string rather than the raw boolean so the facet's options read as
       * words. The shared matcher stringifies (`String(productValue)`), so a raw boolean
       * would force option values of 'true'/'false' and a label map to hide them.
       */
      return scoped.map(p => {
        const variantSizes = ((p as any).variants ?? [])
          .map((v: any) => v.size)
          .filter(Boolean) as string[];
        return {
          ...p,
          sizes: [...new Set(variantSizes)],
          on_metal: (p as any).on_metal_available ? 'Available' : 'Not available',
          // Buyer-facing label ("Readers", not Shopify's warehouse word "Hardware"), and a
          // STRING because a plain `multiSelect` facet renders its options directly as React
          // children — passing {value,label} objects there throws "Objects are not valid as a
          // React child" and the ErrorBoundary swallows the whole page. Only `statusChip`
          // facets (Capability) accept option objects. shopTypeLabel falls back to the raw
          // value, so a type added to Shopify later is unlabelled rather than invisible.
          product_type: shopTypeLabel(String((p as any).category_type ?? '')),
        };
      });
    },
    [data, typeFilter]
  );

  // Usage-based ordering (VRG-151): order each card's best_for_product_types so the categories this
  // brand actually sells (counted by product category_type) come first; categories the
  // brand has no products in keep their authored order. Degrades to no-op if the brand's
  // category vocabulary doesn't match (everything ranks 0).
  const { data: brandProducts } = useProducts({ limit: 250 });
  const normalizeCat = (s: any) => String(s ?? '').trim().toLowerCase().replace(/[\s&]+/g, '_');
  const categoryUsage = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of (brandProducts?.data ?? [])) {
      const key = normalizeCat((p as any).category_type);
      if (key) counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [brandProducts]);
  const sortBestForByUsage = (bestFor: string[] = []) =>
    [...bestFor].sort((a, b) => (categoryUsage[normalizeCat(b)] ?? 0) - (categoryUsage[normalizeCat(a)] ?? 0));

  // Size facet options: only real DIMENSIONAL sizes (mm diameters, composite WxH, and
  // apparel S/M/L). Closure carries descriptive form-variant names ('neck-band',
  // 'cap-disc') in its sizes[] — those aren't sizes, so they're excluded here. Ordered
  // small→big: numeric mm by value first, then apparel S<M<L (alphabetical would put 6mm
  // after 45mm).
  const APPAREL_ORDER = ['S', 'M', 'L'];
  const isDimensionalSize = (s: string) => /\d/.test(s) || APPAREL_ORDER.includes(s);
  const sizeSortKey = (s: string): [number, number] => {
    const ap = APPAREL_ORDER.indexOf(s);
    if (ap >= 0) return [2, ap];
    const m = String(s).match(/(\d+(?:\.\d+)?)/);
    return [1, m ? parseFloat(m[1]) : 9999];
  };
  const sizeOptions = useMemo(() => {
    const all = new Set<string>();
    for (const p of products) for (const s of ((p as any).sizes ?? [])) all.add(s);
    return [...all].filter(isDimensionalSize).sort((a, b) => {
      const ka = sizeSortKey(a), kb = sizeSortKey(b);
      return ka[0] - kb[0] || ka[1] - kb[1];
    });
  }, [products]);

  // Capability filter options — colours come from the SAME palette ramps as the
  // product-card TagCapabilityBadges (auth=purple, trace=blue, sense=orange), so the
  // filter badge text matches the cards exactly in both light + dark. color=[500] drives
  // the alpha fill/border; textColor=[200] (dark) / [700] (light) is the card's text stop.
  const capabilityStatusOptions = useMemo(() => {
    const dark = theme.palette.mode === 'dark';
    const txt = (ramp: any) => (dark ? ramp[200] : ramp[700]);
    const purple = theme.palette.purple, orange = theme.palette.orange, blue = theme.palette.blue, red = theme.palette.red;
    // Order matches the card badges: auth (Verify, Vault) → Trace → Sense (Tamper,
    // Temperature, Moisture, Pressure). Connect is excluded (baseline on every tag).
    return [
      { value: 'Verify', label: 'Verify', color: purple[500], textColor: txt(purple), icon: <ShieldCheckIcon {...CAP_ICON} />, tooltip: LEAF_BLURB.verify },
      { value: 'Vault', label: 'Vault', color: purple[500], textColor: txt(purple), icon: <KeyRoundIcon {...CAP_ICON} />, tooltip: LEAF_BLURB.vault },
      { value: 'Trace', label: 'Trace', color: blue[500], textColor: txt(blue), icon: <RadarIcon {...CAP_ICON} />, tooltip: LEAF_BLURB.trace },
      // Tamper is shown RED (security/tamper-evidence reads better than Sense orange to buyers).
      { value: 'Tamper', label: 'Tamper', color: red[500], textColor: txt(red), icon: <ShieldAlertIcon {...CAP_ICON} />, tooltip: LEAF_BLURB.tamper },
      { value: 'Temperature', label: 'Temperature', color: orange[500], textColor: txt(orange), icon: <ThermometerIcon {...CAP_ICON} />, tooltip: LEAF_BLURB.temperature },
      { value: 'Moisture', label: 'Moisture', color: orange[500], textColor: txt(orange), icon: <DropletsIcon {...CAP_ICON} />, tooltip: LEAF_BLURB.moisture },
      { value: 'Pressure', label: 'Pressure', color: orange[500], textColor: txt(orange), icon: <GaugeIcon {...CAP_ICON} />, tooltip: LEAF_BLURB.pressure },
    ];
  }, [theme.palette.mode, theme.palette.purple, theme.palette.orange, theme.palette.blue, theme.palette.red]);

  const columns: Column[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Title',
        type: 'string',
        width: 250,
        pinned: 'left',
        searchParams: { fields: ['title', 'sku'], preview: 'products' },
        renderCell: ({ row }: { row: any }) => <ProductCell product={row} />,
      },
      {
        // VRG-141/152 faceted browse: filter by capability. StatusChip style (icon +
        // family colour), like the certificate-status filter. Points at the atomic
        // capabilityLeaves (not the combined `capabilities`) so a chip like "Tamper"
        // matches any product carrying tamper, regardless of its other capabilities.
        field: 'capabilityLeaves',
        headerName: 'Capability',
        type: 'statusChip',
        // Render the filter options as capability BADGES (coloured rounded-rect, leaf
        // icon + word) at the same size as the product-page badges — not small pills.
        variant: 'badge',
        options: capabilityStatusOptions,
        width: 160,
        align: 'center',
      },
      {
        // VRG-156 — Scan range = the NFC PHONE-TAP range (near-field, ~cm), size-driven:
        // small tags touch (~1 cm), larger tags tap (~2–4 cm). RFID far-field (~10 m) is a
        // separate modality and is NOT here — it's the Trace capability (filter by Trace).
        // Synthetic facet (no product column); matched in filteredByFilters from the sizes.
        field: 'scanRange',
        headerName: 'Scan range',
        type: 'multiSelect',
        options: SCAN_RANGE_BUCKETS,
        width: 160,
        align: 'center',
        sortable: false,
        sortOptions: false,
      },
      {
        // Price above Best for in the filter panel (facets follow column order).
        field: 'unit_price',
        headerName: 'Unit price',
        type: 'number',
        // Slide by the cent and show clean currency ($1.55, not 1.54775).
        step: 0.01,
        formatValue: (v: number) => toDollar(v),
      },
      {
        // Size facet — multiSelect over the product's discrete size strings. Sizes are
        // non-uniform across forms (mm diameters, S/M/L, composite WxH, named formats),
        // so a chip multiSelect is the right control, not a slider (VRG-149).
        field: 'sizes',
        headerName: 'Size',
        type: 'multiSelect',
        options: sizeOptions,
        // options are pre-ordered small→big; keep that order (don't re-sort alphabetically).
        sortOptions: false,
        sortable: false,
      },
      {
        /*
         * Product type — Tags vs Readers. The catalog mixes the Vera Tag Desk Reader in
         * with every tag, and until now the ONLY way to separate them was arriving with
         * `?type=Hardware` on the URL: the chip that renders is removable but not
         * settable, so a shopper already in the shop had no way to say "just readers".
         *
         * Composes with that URL param rather than replacing it — the param pre-scopes the
         * set (so the other facets offer values that actually match), this filters within
         * it, and the chip stays visible so a contradictory pair reads as filtered rather
         * than broken.
         *
         * Values are Shopify's raw `productType`; labels come from SHOP_TYPE_LABEL because
         * "Hardware" is warehouse vocabulary and a shopper is looking for "Readers".
         */
        field: 'product_type',
        headerName: 'Product type',
        type: 'multiSelect',
        // Plain strings — see `product_type` in the products memo for why this cannot be
        // a list of {value,label} objects.
        options: [
          shopTypeLabel(SHOP_PRODUCT_TYPE.TAG),
          shopTypeLabel(SHOP_PRODUCT_TYPE.HARDWARE),
        ],
        sortOptions: false,
        sortable: false,
      },
      {
        /*
         * On-metal. NFC detunes against metal, so "can this go on a metal surface?" is a
         * genuine buying constraint and not a nice-to-have — a buyer tagging watch cases or
         * bottles has no other way to narrow. `on_metal_available` was already in the
         * payload; only the facet was missing.
         *
         * Deliberately NOT shown on the product cards: it is a configurator OPTION, not a
         * capability, and putting options in the capability row would imply they are
         * properties rather than choices, and make cards non-comparable.
         */
        field: 'on_metal',
        headerName: 'On-metal',
        type: 'multiSelect',
        options: ['Available', 'Not available'],
        sortOptions: false,
        sortable: false,
      },
      {
        field: 'best_for_product_types',
        headerName: 'Best for',
        type: 'multiSelect',
        width: 150,
        align: 'center',
        renderCell: ({ value, filter }: { value: any; filter: any }) => (
          <CategoryCell category={value} filter />
        ),
      },
    ],
    [sizeOptions, capabilityStatusOptions]
  );

  // Filter products based on search
  const filteredBySearch = useMemo(() => {
    if (!search.trim()) return products;
    return products.filter(product =>
      product.title.toLowerCase().includes(search.toLowerCase())
    );
  }, [products, search]);

  // Filter products based on filters
  const filteredByFilters = useMemo(() => {
    if (filters.length === 0) return filteredBySearch;

    return filteredBySearch.filter(product => {
      return filters.every(filter => {
        // VRG-156 Scan range — a synthetic facet with no product column. A product's
        // NFC phone-tap range spans its sizes (touch for small, tap for larger), so it
        // matches when ANY selected bucket overlaps its size-derived buckets.
        if (filter.field === 'scanRange') {
          const buckets = scanBucketsForSizes(((product as any).sizes ?? []) as string[]);
          return (filter.value as string[]).some(v => buckets.has(v));
        }

        const productValue = product[filter.field as keyof typeof product];

        // Numeric range (the Unit price slider emits >= / <= with a single number).
        if (filter.operator === '>=') {
          return Number(productValue) >= Number(filter.value);
        }
        if (filter.operator === '<=') {
          return Number(productValue) <= Number(filter.value);
        }

        if (filter.operator === 'isAnyOf') {
          const values = filter.value as string[];
          if (typeof productValue === 'string') {
            return values.includes(productValue);
          } else if (Array.isArray(productValue)) {
            return productValue.some(category => values.includes(String(category)));
          } else {
            return values.includes(String(productValue));
          }
        }
        return true;
      });
    });
  }, [filteredBySearch, filters]);

  // Sort products
  const sortedProducts = useMemo(() => {
    if (sort.length === 0) return filteredByFilters;

    const sortOrders = sort.reduce((acc, curr) => {
      acc[curr.field] = curr.sort;
      return acc;
    }, {} as Record<string, 'asc' | 'desc'>);

    return multiSort(filteredByFilters, sortOrders);
  }, [filteredByFilters, sort]);

  // Reset visible count when search/filters/sort/type change
  useEffect(() => {
    setVisibleCount(PAGE);
  }, [search, filters, sort, typeFilter]);

  // Visible slice for progressive render
  const visibleProducts = useMemo(
    () => sortedProducts.slice(0, visibleCount),
    [sortedProducts, visibleCount]
  );

  const handleSortChange = (col: string | SortItem[], direction?: 'asc' | 'desc' | 'clear', override?: boolean) => {
    // Handle array input (override whole sort)
    if (Array.isArray(col)) {
      setSort(col);
      return;
    }

    // Handle clear all
    if (col === 'all' && direction === 'clear') {
      setSort([]);
      return;
    }

    // Handle single column sort
    if (typeof col === 'string' && direction && direction !== 'clear') {
      if (override) {
        setSort([{ field: col, sort: direction }]);
      } else {
        setSort(prevSort => {
          const existingIndex = prevSort.findIndex(s => s.field === col);
          if (existingIndex !== -1) {
            const newSort = [...prevSort];
            newSort[existingIndex].sort = direction;
            return newSort;
          } else {
            return [...prevSort, { field: col, sort: direction }];
          }
        });
      }
    }
  };

  const handleFiltersChange = (value: FilterItem[]) => {
    setFilters(value);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  // Fill-height: size the scroll container so its bottom sits the same 24px above the
  // viewport bottom as the page's L/R gutter (§22).
  //
  // This was a hand-rolled `innerHeight - top - 24` on a `window.resize` listener — the
  // exact third copy `useFillBottom`'s docblock was written about, and it had the exact
  // staleness described there: almost nothing that moves this pane's top is a window
  // resize. Mounting or clearing the `?type=` chip ABOVE the container shifted its top by
  // 52px with the viewport unchanged, so no listener fired and the height stayed at its
  // old value — the pane then either overhung the viewport (the empty state, centred in
  // it, sat below the fold) or stopped 52px short of the gutter. The shared hook watches
  // the ancestor chain for exactly that reflow, and additionally handles CSS `zoom`,
  // which the preview pane sets on `body` and which fires no resize event at all.
  const { ref: scrollContainerRef, height: measuredFillHeight } = useFillBottom<HTMLDivElement>({
    minHeight: 240,
    // Belt and braces alongside the hook's observers: the values that change what is
    // rendered inside the pane, and (typeFilter) what is rendered above it.
    deps: [isLoading, typeFilter, sortedProducts.length],
  });
  const fillHeight = measuredFillHeight ?? 400;

  // Edge fades. This was a hand-rolled scroll/resize listener plus an inline
  // `linear-gradient` mask -- a duplicate of `@repo/ui`'s `useScrollFade`, which
  // already owned the same bookkeeping for the filter facet list, the address-icon
  // grid and the activity timeline (§36). The shared hook also brought the
  // MutationObserver this lacked, which matters here because a page of
  // infinite-scroll results can arrive without any resize firing.
  //
  // BOTTOM ONLY (the hook's default). This briefly passed `edges: 'both'` to stop the
  // grid clipping hard against its top edge, but that made the shop the ONLY surface in
  // the app fading at the top -- every other consumer of this hook takes the default --
  // so the top fade read as an unexplained gradient under the toolbar rather than as an
  // affordance. Consistency wins: a hard top edge is what the rest of the app does, and
  // the scrollbar already says there is more above.
  const { ref: fadeRef, fadeSx } = useScrollFade<HTMLDivElement>({
    fadeHeight: 48,
  });

  // One element, two hooks that each want a ref (fill-height sizing + the fades),
  // so the refs are merged at the attachment point. Callback refs are assigned
  // before layout effects run in the same commit, so both hooks still see the node
  // on their first measure.
  const setScrollContainer = useCallback(
    (el: HTMLDivElement | null) => {
      scrollContainerRef.current = el;
      fadeRef.current = el;
    },
    [scrollContainerRef, fadeRef]
  );

  // Infinite scroll: IntersectionObserver on the sentinel div
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!sentinel || !scrollContainer) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount(c => Math.min(c + PAGE, sortedProducts.length));
        }
      },
      { root: scrollContainer, rootMargin: '120px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sortedProducts.length, visibleCount]);

  return (
    <>
      <ActionLine
        showBulkActions={false}
        columns={columns}
        rows={products}
        sort={sort}
        showSort={true}
        showFilters={true}
        onSortChange={handleSortChange}
        onFiltersChange={handleFiltersChange}
        onSearchChange={handleSearchChange}
      />
      {/* The type filter arrived from a link, so it has to be VISIBLE and removable —
          otherwise a shop showing one product looks broken rather than filtered. Its own
          chip, not a drawer filter, for the reason given where `typeFilter` is read. */}
      {typeFilter && (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{
            // fit-content: a row Stack would otherwise stretch its border the full page width.
            width: 'fit-content',
            mt: 4,
            pl: 3,
            pr: 1,
            py: 1,
            borderRadius: 45,
            border: `1px solid ${theme.palette.primary[100]}`,
          }}
        >
          <Typography variant="body3" color="text.secondary">
            Showing
          </Typography>
          <Typography variant="body3">{shopTypeLabel(typeFilter)}</Typography>
          {/* X, not a trash can: this clears a selection, it destroys nothing (§38). */}
          <IconButton
            size="small"
            aria-label={`Clear the ${shopTypeLabel(typeFilter)} filter`}
            onClick={clearTypeFilter}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': { color: theme.palette.text.primary },
            }}
          >
            <XIcon strokeWidth={1} size={16} />
          </IconButton>
        </Stack>
      )}
      {/* The scroll container mounts in EVERY state — skeleton, empty, and loaded. It is
          what gives the tab its measured fill height, so keeping it out of the DOM while
          loading meant the height could only be measured once the products arrived, and
          the page reflowed around the swap. Now only its CONTENTS change. */}
        {/* Plain `div` + `style`, deliberately: `marginRight: -24` below is raw px, and
            MUI's `sx` would run a numeric margin through the theme spacing transform
            (-24 -> -96px). `fadeSx`'s keys are all valid inline-style properties, so it
            spreads here unchanged. */}
        <div
          ref={setScrollContainer}
          style={{
            height: fillHeight,
            overflowY: 'auto',
            // Put the scrollbar to the RIGHT of the cards without moving the cards.
            //
            // macOS renders overlay scrollbars: they take no layout width and are
            // painted over the content box, so a container has to reserve the room
            // itself. Reserving it with padding alone worked but pulled the cards
            // 24px left of the Filter/Sort buttons above them, which read as a gap.
            //
            // So the scroller is widened into the page's right gutter by the same
            // amount it pads: the content box (and therefore the cards' right edge)
            // stays put and aligned with the toolbar, while the bar is painted out in
            // the gutter that was previously empty.
            marginRight: -24,
            paddingRight: 24,
            ...fadeSx,
          }}
        >
          {/* First load only. `isLoading` is react-query's "no data yet AND fetching", so a
              background refetch (this query is `staleTime: 0` + refetch-on-focus) leaves the
              products it already has on screen rather than flashing a skeleton over them. */}
          {isLoading ? (
            <ShopSkeleton />
          ) : /* A narrowed catalog can legitimately come back empty — the store stocks one
                 reader today, and a type could be out of stock or unpublished tomorrow. Say so,
                 with the way out, instead of leaving the page blank (which reads as a fault). */
          sortedProducts.length === 0 ? (
            /* No local padding override. This carried `py: 16`, which inside a
               fill-height, centre-justified box did nothing for the centring but did
               cancel the shared EmptyState's own bottom padding — the 20px lift that
               keeps an icon-topped block from reading low — and at a short viewport
               pushed the block out of its own content box. Same treatment as every
               other EmptyState in the app now. */
            <EmptyState
              icon={<PackageSearchIcon strokeWidth={1} size={28} color={theme.palette.text.disabled} />}
              title="Nothing to show here"
              text={
                typeFilter
                  ? `The shop has no ${shopTypeLabel(typeFilter).toLowerCase()} available right now.`
                  : 'No products match what you have searched or filtered for.'
              }
              /* The custom-tag offer belongs here too, and arguably most of all: a filter
                 combination the catalog cannot satisfy is the clearest possible signal that
                 someone wants a tag we do not stock. Turns a dead end into a lead. */
              buttons={
                <>
                  {typeFilter && (
                    <Button variant="secondary" disableElevation onClick={clearTypeFilter}>
                      Show everything
                    </Button>
                  )}
                  <Button variant="secondary" disableElevation onClick={() => setCustomTagOpen(true)}>
                    Tell us what you need
                  </Button>
                </>
              }
            />
          ) : (
          <Grid container spacing={4}>
            {visibleProducts.map((product, index) => (
              <Fade in={true} timeout={200} key={product.id}>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  sx={{ cursor: 'pointer', minHeight: 350 }}
                  onClick={() => navigate(ROUTES.TAGS_SHOP_PRODUCT(product.sku), { state: referrer })}
                >
                  <TagsShopProductPreview product={{ ...product, best_for_product_types: sortBestForByUsage(product.best_for_product_types) }} />
                </Grid>
              </Fade>
            ))}
            {/* VRG-266 — "we can make that", at the end of the catalog.
                Gated on having paged to the END: with infinite scroll this would otherwise
                sit mid-list after the first 12, offering a custom tag to someone who has
                not yet seen the stocked ones. It belongs at the point the catalog runs out,
                which is the moment the offer is actually relevant. */}
            {visibleProducts.length === sortedProducts.length && (
              <Grid item xs={12} sm={6} md={4} lg={3} sx={{ minHeight: 350 }}>
                <Stack
                  role="button"
                  tabIndex={0}
                  onClick={() => setCustomTagOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCustomTagOpen(true); }
                  }}
                  spacing={3}
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    height: '100%',
                    p: 6,
                    textAlign: 'center',
                    cursor: 'pointer',
                    borderRadius: 3,
                    /*
                     * A CARD, like the products beside it — not a dashed outline.
                     *
                     * It was `1px dashed primary[100]` on a transparent fill, and in
                     * light mode that is a pale dash on the page's own colour: barely
                     * visible, and the one tile in the grid that looked unfinished
                     * rather than deliberate. The dash was carrying "this is not a
                     * product you can buy", which it does not need to carry — no
                     * image, no price, an icon and its own button already say that,
                     * and the house keeps the dashed outline for DROPZONES, where it
                     * means "put something here" (working-conventions §31).
                     *
                     * So it takes the same surface and hairline its neighbours take,
                     * and the grid reads as one rhythm. The hover treatment is
                     * unchanged — it is still an action card (§34), so it keeps the
                     * brand border and ring rather than a selectable tint.
                     */
                    border: `1px solid ${theme.palette.primary[100]}`,
                    backgroundColor: theme.palette.background.paper,
                    transition: 'border-color 150ms, box-shadow 150ms',
                    // Action card, not a selectable one: it does something on click and has
                    // no persistent selected state, so it takes the brand border + ring
                    // rather than the selectable tint. Focus-visible is mandatory, not
                    // decorative — this is a real control.
                    '&:hover, &:focus-visible': {
                      borderColor: theme.palette.brand[600],
                      boxShadow: `0 0 0 3px ${theme.palette.brand['600-6%']}`,
                      outline: 'none',
                    },
                  }}
                >
                  <PackageSearchIcon strokeWidth={1} size={28} color={theme.palette.text.disabled} />
                  <Stack spacing={2} alignItems="center">
                    <Typography variant="body1">Need something custom?</Typography>
                    <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      We can manufacture bespoke tags. Tell us what you need and we'll come
                      back with options.
                    </Typography>
                  </Stack>
                  <Button variant="secondary" disableElevation onClick={(e: any) => { e.stopPropagation(); setCustomTagOpen(true); }}>
                    Tell us what you need
                  </Button>
                </Stack>
              </Grid>
            )}
          </Grid>
          )}
          {/* Infinite-scroll sentinel — observed by IntersectionObserver. Only while there
              is something to page through: with an empty (or not-yet-loaded) list the
              observer fires immediately and clamps `visibleCount` to 0, which would then
              hold the first page back until a scroll nudged it again. */}
          {!isLoading && sortedProducts.length > 0 && (
            <div ref={sentinelRef} style={{ height: 1 }} />
          )}
        </div>
      {/* Mounted at the root, not inside the card: the empty state opens it too, and that
          branch renders when the card does not. */}
      <CustomTagModal open={customTagOpen} onClose={() => setCustomTagOpen(false)} />
    </>
  );
};
